const MONTHS_UPPER = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
const SUMMARY_PERIOD_NAMES = new Set(["FY", "YTD", "YTG"]);

/**
 * Normalises period labels so Retail 5-4-4 names (`P1 - Apr`) can match
 * calendar-month names (`Apr`) when comparing forecast years.
 */
export const normalizePeriodName = (name: string | null | undefined): string => {
    if (!name) {
        return "";
    }

    const trimmed = String(name).trim();
    const summaryName = trimmed.toUpperCase();
    if (SUMMARY_PERIOD_NAMES.has(summaryName)) {
        return summaryName;
    }

    return trimmed.replace(/^P\d+\s*-\s*/i, "").toLowerCase();
};

const isSummaryPeriodName = (name: string | null | undefined): boolean =>
    SUMMARY_PERIOD_NAMES.has(normalizePeriodName(name));

/**
 * Resolves a comparison series index by normalised period name, then by
 * fiscal-slot index when labels cannot be matched (e.g. weekly `dd-MM-yy`).
 * Positional fallback is limited to non-summary periods so a surplus 53rd week
 * does not resolve to the comparison FY total.
 */
export const findComparisonPeriodIndex = (
    comparisonPeriodNames: string[],
    mainPeriodName: string,
    fallbackIndex: number
): number => {
    const normalizedMain = normalizePeriodName(mainPeriodName);
    const matchIndex = comparisonPeriodNames.findIndex(
        (name) => normalizePeriodName(name) === normalizedMain
    );

    if (matchIndex !== -1) {
        return matchIndex;
    }

    const fallbackName = comparisonPeriodNames[fallbackIndex];
    if (!fallbackName || isSummaryPeriodName(fallbackName)) {
        return -1;
    }

    return fallbackIndex;
};

/**
 * Returns fiscal-order month labels in uppercase.
 * For Retail 5-4-4 calendars, labels are prefixed with fiscal period numbers (P1..P12).
 */
export const generateMonthLabels = (startMonth: number, fiscalCalendarRuleSet?: number): string[] => {
    const rotationIndex = (startMonth - 1 + 12) % 12;
    const orderedMonths = MONTHS_UPPER
        .slice(rotationIndex)
        .concat(MONTHS_UPPER.slice(0, rotationIndex));

    if (fiscalCalendarRuleSet !== 1) {
        return orderedMonths;
    }

    return orderedMonths.map((month, index) => `P${index + 1} - ${month}`);
};

